/**
 * @file 결재 API
 * 결재 문서의 생성·조회·처리(승인/반려/보류), 첨부파일 관리, 관리자 전체 조회 등을 처리합니다.
 */

import { api } from "../../../util/api";

const API_BASE = process.env.REACT_APP_API_BASE_URL;

export const approvalApi = {
    /** 결재 문서 생성
     * @param {object} data - 결재 문서 데이터
     */
    create:           (data)           => api.post(`${API_BASE}/approval`, data),

    /** 수신 결재함 조회 (내가 결재해야 할 문서) */
    inbox:            ()               => api.get(`${API_BASE}/approval/inbox`),

    /** 발신 결재함 조회 (페이지네이션)
     * @param {number} [page=0] - 페이지 번호 (0-based)
     * @param {number} [size=20] - 페이지 크기
     */
    sent:             (page = 0, size = 20) => api.get(`${API_BASE}/approval/sent`,      { page, size }),

    /** 관련 결재함 조회 (페이지네이션) */
    related:          (page = 0, size = 20) => api.get(`${API_BASE}/approval/related`,   { page, size }),

    /** 완료 결재함 조회 (페이지네이션) */
    completed:        (page = 0, size = 20) => api.get(`${API_BASE}/approval/completed`, { page, size }),

    /** 결재 문서 상세 조회
     * @param {number} id - 결재 문서 ID
     */
    detail:           (id)             => api.get(`${API_BASE}/approval/${id}`),

    /** 결재 승인
     * @param {number} id - 결재 문서 ID
     * @param {string} comment - 승인 의견
     */
    approve:          (id, comment)    => api.post(`${API_BASE}/approval/${id}/approve`, { comment }),

    /** 결재 반려
     * @param {number} id - 결재 문서 ID
     * @param {string} comment - 반려 사유
     */
    reject:           (id, comment)    => api.post(`${API_BASE}/approval/${id}/reject`, { comment }),

    /** 결재 보류
     * @param {number} id - 결재 문서 ID
     * @param {string} comment - 보류 사유
     */
    hold:             (id, comment)    => api.post(`${API_BASE}/approval/${id}/hold`, { comment }),

    /** 결재 첨부파일 업로드 (multipart/form-data)
     * @param {number} id - 결재 문서 ID
     * @param {File} file - 업로드할 파일
     */
    uploadAttachment: (id, file)       => {
        const form = new FormData();
        form.append("file", file);
        return api.upload(`${API_BASE}/approval/${id}/attachment`, form);
    },

    /** 결재 첨부파일 삭제
     * @param {number} attachmentId - 첨부파일 ID
     */
    deleteAttachment: (attachmentId)   => api.delete(`${API_BASE}/approval/attachment/${attachmentId}`),

    /** 첨부 미리보기 (blob). PDF·이미지·엑셀·텍스트는 원본, 오피스는 서버 PDF 변환. */
    previewAttachment: (attachmentId)  => api.download(`${API_BASE}/approval/attachment/${attachmentId}/preview`),

    /** 결재 문서 삭제 (기안 취소)
     * @param {number} id - 결재 문서 ID
     */
    deleteApproval:   (id)             => api.delete(`${API_BASE}/approval/${id}`),

    /** 관리자용 전체 결재 목록 조회 (페이지네이션)
     * @param {string} status - 결재 상태 필터 ("ALL" 또는 특정 상태값)
     * @param {string} type - 결재 유형 필터 ("ALL" 또는 특정 유형값)
     * @param {number} [page=0] - 페이지 번호 (0-based)
     * @param {number} [size=20] - 페이지 크기
     */
    adminAll: (status, type, page = 0, size = 20, submitter = "", approver = "") => api.get(`${API_BASE}/approval/admin/all`, {
        ...(status && status !== "ALL" ? { status } : {}),
        ...(type   && type   !== "ALL" ? { type }   : {}),
        // 기안자/결재자 검색을 서버로 — 전체 결재에 걸쳐 매칭(현재 페이지만 필터하던 버그 해소)
        ...(submitter && submitter.trim() ? { submitter: submitter.trim() } : {}),
        ...(approver  && approver.trim()  ? { approver:  approver.trim()  } : {}),
        page,
        size,
    }),

    /** 관리자 강제 승인 — 결재선 무시 (APPROVAL_ADMIN) */
    adminApprove: (id, comment) => api.post(`${API_BASE}/approval/admin/${id}/approve`, { comment }),

    /** 관리자 강제 거절 — 결재선 무시 (APPROVAL_ADMIN) */
    adminReject:  (id, comment) => api.post(`${API_BASE}/approval/admin/${id}/reject`, { comment }),
};
